<template>

  <!-- Courses Admin Start -->
  <div class="section overflow-hidden position-relative min-height" id="wrapper">

    <!-- Page Content Wrapper Start -->
    <div class="page-content-wrapper padding-left-0">
      <div class="container-fluid custom-container">

        <!-- Message Start -->
        <div class="dashboard-head" v-if='user'>
          <div class="message-content">
            <h2>Hello {{user.first_name}}!</h2>
            <h3>Welcome to your COLREGs Dashboard!</h3>
            <p>Navigate your final tests, track your progress, and refine your maritime knowledge—all in one place.</p>
          </div>
        </div>
        <!-- Message End -->

        <!-- Packages Start (Only show for individual users without package) -->
        <div v-if='!user_pkg && isIndividualUser' class="section section-padding mt-n10">
          <div class="container row gx-12">

            <div class="col-lg-12">
            <!-- Section Title Start -->
            <div class="section-title shape-03 ">
              <h3>You are currently on a free trial. Please purchase a package from the options below to continue using our services</h3>
            </div>
            <!-- Section Title End -->

            <div class="row gx-10">
              <div class="col-lg-4" v-for="pkg in pkgs" :key='pkg._id'>
                <!-- Courses Details Sidebar Start -->
                <div class="sidebar">
                  <!-- Sidebar Widget Information Start -->
                  <div class="sidebar-widget widget-information">
                    <div class="info-price">
                      <span class="price">{{pkg.package_name}}</span>
                      <span class="package-price">£{{pkg.sale_price}}</span>
                    </div>
                    <div class="info-list">
                      <ul>
                        <li><i class="icofont-book-alt"></i> <strong>Questions</strong> <span>{{pkg.total_questions}}</span></li>
                        <li><i class="icofont-book"></i> <strong>Practice Test</strong> <span>Yes</span></li>
                        <li><i class="icofont-clock-time"></i> <strong>Final Test Attempts</strong> <span>{{pkg.mock_attempts_allowed}}</span></li>
                      </ul>
                    </div>
                    <div class="info-btn">
                      <button class="btn btn-primary btn-hover-dark" @click='upgrade(pkg._id)'>Purchase Now</button>
                    </div>
                  </div>
                  <!-- Sidebar Widget Information End -->
                </div>
                <!-- Courses Details Sidebar End -->
              </div>
            </div>
          </div>
          </div>
        </div>
        <!-- Packages End -->

        <!-- Organization Manager Package Section -->
        <div v-if='!user_pkg && isOrgManager' class="section section-padding mt-n10">
          <div class="container row gx-12">
            <div class="col-lg-12">
              
              <!-- No Custom Package Yet -->
              <div v-if="!orgCustomPackage" class="section-title shape-03">
                <h3>No custom package has been assigned to your organization yet.</h3>
                <p>Please contact our support team or submit a package request to get a custom package created for your organization.</p>
                <div class="mt-3">
                  <router-link to="/retention-package" class="btn btn-primary btn-hover-dark">
                    Request Custom Package
                  </router-link>
                  <a href="mailto:admin@thecolregs.com" class="btn btn-secondary ml-2">
                    Contact Support
                  </a>
                </div>
              </div>

              <!-- Custom Package Available -->
              <div v-else>
                <div class="section-title shape-03">
                  <h3>Your custom organization package is ready for purchase!</h3>
                </div>
                
                <div class="row gx-10">
                  <div class="col-lg-5">
                    <div class="sidebar">
                      <div class="sidebar-widget widget-information custom-package">
                        <div class="info-price org-pack-name">
                          <span class="price org-package-name">{{ formatPackageName(orgCustomPackage.package_name) }}</span>
                        </div>
                        <div class="info-list">
                          <ul>
                            <li><i class="icofont-users-alt-4"></i> <strong>Price</strong> <span>{{ orgCustomPackage.sale_price }}</span></li>
                            <li><i class="icofont-users-alt-4"></i> <strong>Users Allowed</strong> <span>{{ orgCustomPackage.users_allowed }}</span></li>
                            <li><i class="icofont-book-alt"></i> <strong>Questions</strong> <span>{{ orgCustomPackage.total_questions }}</span></li>
                            <li><i class="icofont-book"></i> <strong>Practice Test</strong> <span>Yes</span></li>
                            <li><i class="icofont-clock-time"></i> <strong>Test Duration</strong> <span>{{ orgCustomPackage.duration }} mins</span></li>
                            <li><i class="icofont-refresh"></i> <strong>Final Test Attempts</strong> <span>{{ orgCustomPackage.mock_attempts_allowed }}</span></li>
                          </ul>
                        </div>
                        <div class="info-btn">
                          <button class="btn btn-primary btn-hover-dark" @click='upgrade(orgCustomPackage._id)'>
                            Purchase Now
                          </button>
                        </div>
                        
                        <!-- Refresh Button if package still showing after purchase -->
                        <div class="info-note mt-3" style="text-align: center; font-size: 13px; color: #666;">
                          <p>Just completed payment? <button @click="refreshPage" class="btn-link" style="color: #309255; text-decoration: underline; background: none; border: none; cursor: pointer; padding: 0;">Click here to refresh</button></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Organization Member Message (No package purchase option) -->
        <div v-if='!user_pkg && !isIndividualUser && !isOrgManager' class="section section-padding mt-n10">
          <div class="container row gx-12">
            <div class="col-lg-12">
              <div class="section-title shape-03">
                <h3>Your organization administrator manages package subscriptions. Please contact them to get access to a package.</h3>
              </div>
            </div>
          </div>
        </div>

        <!-- Your Packages Start -->
        <div class="section section-padding mt-n10 padding-top-50">
          <div class="container row gx-10">

            <div class="col-lg-4">
              <!-- Courses Details Sidebar Start -->
              <div class="sidebar">
                <!-- Sidebar Widget Information Start -->
                <div class="sidebar-widget widget-information selected-package-card">
                  <div class="info-price">
                    <span class="package-price">Your Selected Package</span>
                    <span class="price" v-if='user_pkg'>{{formatPackageName(user_pkg.pkg_subscribed)}}</span>
                    <span class="price" v-else>Free Trial</span>
                  </div>
                  
                  <!-- Package with Subscription Details -->
                  <div class="info-list detailed-list" v-if='user_pkg'>
                    <ul>
                      <!-- For Custom/Organization Packages -->
                      <li v-if="user_pkg.package_type === 'custom' && user_pkg.users_allowed" class="highlight-item">
                        <i class="icofont-users-alt-4"></i> 
                        <strong>Team Members Allowed</strong> 
                        <span class="value-badge color-white">{{ user_pkg.users_allowed }}</span>
                      </li>
                      
                      <li>
                        <i class="icofont-book-alt"></i> 
                        <strong>Total Questions in Pool</strong> 
                        <span>{{ user_pkg.tot_ques }}</span>
                      </li>
                      
                      <li>
                        <i class="icofont-clock-time"></i> 
                        <strong>Test Duration</strong> 
                        <span>{{ user_pkg.duration }} minutes</span>
                      </li>
                      
                      <li>
                        <i class="icofont-refresh"></i> 
                        <strong>Final Test Attempts (Per User)</strong> 
                        <span>{{ user_pkg.mock_attempts_allowed }}</span>
                      </li>
                      
                      <li>
                        <i class="icofont-ui-check"></i> 
                        <strong>Tests Completed</strong> 
                        <span>{{ user_pkg.total_mocks_attempted }} / {{ user_pkg.mock_attempts_allowed }}</span>
                      </li>
                      
                      <li>  
                        <i class="icofont-book"></i> 
                        <strong>Practice Test</strong> 
                        <span class="unlimited-badge color-white">Unlimited</span>
                      </li>
                      
                      <li v-if="user_pkg.package_price">
                        <i class="icofont-cur-rupee"></i> 
                        <strong>Package Price</strong> 
                        <span>£{{ user_pkg.package_price }}</span>
                      </li>
                      
                      <li class="date-item">
                        <i class="icofont-calendar"></i> 
                        <strong>Valid From</strong> 
                        <span>{{ user_pkg.subscription_start }}</span>
                      </li>
                      
                      <li class="date-item">
                        <i class="icofont-calendar"></i> 
                        <strong>Valid Until</strong> 
                        <span>{{ user_pkg.subscription_end }}</span>
                      </li>
                      
                      <li v-if="user_pkg.payment_status" class="status-item">
                        <i class="icofont-verification-check"></i> 
                        <strong>Payment Status</strong> 
                        <span class="badge-success color-white">{{ capitalizeFirst(user_pkg.payment_status) }}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <!-- Free Trial Details -->
                  <div class="info-list" v-else>
                    <ul>
                      <li><i class="icofont-book-alt"></i> <strong>Questions</strong> <span>10</span></li>
                      <li><i class="icofont-book"></i> <strong>Practice Test</strong> <span>Yes</span></li>
                      <li><i class="icofont-clock-time"></i> <strong>Final Test Attempts</strong> <span>No</span></li>
                    </ul>
                  </div>
                </div>
                <!-- Sidebar Widget Information End -->
              </div>
              <!-- Courses Details Sidebar End -->
            </div>

            <div class="col-lg-8">
              <!-- Courses Resources Start -->
              <div class="courses-resources user-home widget-information">
                <h4 class="title">Here is what all you can do with your account</h4>

                <!-- Resources Wrapper Start -->
                <div class="resources-wrapper">
                  <div class="row row-cols-xl-4 row-cols-md-3 row-cols-2">
                    <div class="col">

                      <!-- Single Resources Start -->
                      <div class="single-resources">
                        <div class="resources-icon">
                          <router-link to="/dashboard/organization/user-upload">
                            <i class="icofont-upload-alt dashboard-icon"></i>
                          </router-link>
                        </div>
                        <h5 class="title"><router-link to="/dashboard/organization/user-upload">Add Team Members</router-link></h5>
                      </div>
                      <!-- Single Resources Start -->

                    </div>
                    <div class="col">

                      <!-- Single Resources Start -->
                      <div class="single-resources">
                        <div class="resources-icon">
                          <router-link to="/dashboard/organization/org-users">
                            <i class="icofont-users-alt-5 dashboard-icon"></i>
                          </router-link>
                        </div>
                        <h5 class="title"><router-link to="/dashboard/organization/org-users">My Team</router-link></h5>
                      </div>
                      <!-- Single Resources Start -->

                    </div>
                    <div class="col">

                      <!-- Single Resources Start -->
                      <div class="single-resources">
                        <div class="resources-icon">
                          <router-link to="/dashboard/organization/profile">
                            <i class="icofont-user dashboard-icon"></i>
                          </router-link>
                        </div>
                        <h5 class="title"><router-link to="/dashboard/organization/profile">Profile</router-link></h5>
                      </div>
                      <!-- Single Resources Start -->

                    </div>
                  </div>
                </div>
                <!-- Resources Wrapper End -->

              </div>
              <!-- Courses Resources End -->
            </div>


          </div>
        </div>
        <!-- Packages End -->

      </div>
    </div>
    <!-- Page Content Wrapper End -->
  </div>
  
</template>

<script>
import { jwtDecode } from "jwt-decode";
import { getAllPackageDetails, getOrgManagerPackage } from '@/services/packageService';
import { getUserPackageDetails } from '@/services/subscriptionService';
import { getUserDetails } from '@/services/userService';

export default {
  name: 'UserDashboard',
  data() {
    return {
      pkgs: [],
      message: null,
      user_pkg: null,
      token_info: null,
      user: null,
      isIndividualUser: false,
      isOrgManager: false,
      orgCustomPackage: null,
      loading: false
    };
  },

  computed: {
    shouldShowPurchaseSection() {
      return !this.user_pkg && (this.isIndividualUser || this.isOrgManager);
    }
  },

  watch: {
    user_pkg(newVal) {
      console.log('User package updated:', newVal);
      if (newVal) {
        // User has a package now, ensure purchase sections are hidden
        this.loading = false;
      }
    }
  },

  mounted() {
    const decoded = jwtDecode(localStorage.getItem('access_token'));
    this.token_info = decoded;
    this.getUserDetail();
    this.getUserPackageDetail();
  },

  methods: {

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    formatPackageName(name) {
      if (!name) return '';
      // Display only, does not affect the stored value: strip trailing numeric ID suffix (e.g. "hu_package_1783780661126" -> "hu_package")
      return name.replace(/_\d+$/, '');
    },

    refreshPage() {
      window.location.reload();
    },

    async getUserDetail(){
      try {
        this.user = await getUserDetails(this.token_info.email);
        
        // Check if user is individual or organization
        this.isIndividualUser = this.user.org_or_ind === 'individual';
        this.isOrgManager = this.user.org_or_ind === 'organisation' && this.user.role === 'manager';
        
        // Load packages based on user type
        if (this.isIndividualUser && !this.user_pkg) {
          await this.getPackageAllDetail();
        } else if (this.isOrgManager && !this.user_pkg) {
          await this.getOrgCustomPackage();
        }
        
        console.log('User details:', this.user);
        console.log('Is individual user:', this.isIndividualUser);
        console.log('Is org manager:', this.isOrgManager);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    },

    async getOrgCustomPackage() {
      try {
        const result = await getOrgManagerPackage(this.user.email);
        if (result.hasPackage) {
          this.orgCustomPackage = result.package;
          console.log('Organization custom package:', this.orgCustomPackage);
        } else {
          console.log('No custom package found for this organization');
        }
      } catch (error) {
        console.error('Error fetching org custom package:', error);
      }
    },

    async upgrade(pkg_id) {
      // Simply navigate - Checkout.vue handles everything
      this.$router.push(`/checkout/${pkg_id}`);
    },

    async getUserPackageDetail() {
      try {
        const res = await getUserPackageDetails(this.token_info.id);
        this.user_pkg = res.data;
        console.log('User package:', this.user_pkg);
      } catch (error) {
        console.error('Error fetching user package:', error);
        // If 500 error, user might be org member or has no subscription
        // Set user_pkg to null and let the UI handle it
        this.user_pkg = null;
      }
    },

    async forceReloadData() {
      console.log('Force reloading user data...');
      await this.getUserPackageDetail();
      await this.getUserDetail();
    },

    gotoTestEnv(test_type) {
      if(this.user_pkg && this.user_pkg.sub_id){
        this.$router.push(`mock-test/${this.user_pkg.sub_id}/${this.token_info.id}/${test_type}`);  
      }else{
        this.$router.push(`mock-test/free/${this.token_info.id}/${test_type}`);
      }
    },

    async getPackageAllDetail() {
      try {
        const res = await getAllPackageDetails();
        
        // IMPORTANT: Filter out custom packages - only show standard packages to individual members
        // Custom packages have type='custom' OR have an organisation field set
        this.pkgs = res.data.filter(pkg => {
          return pkg.type !== 'custom' && !pkg.organisation;
        });
        
        console.log('All packages from API:', res.data);
        console.log('Filtered packages (standard only):', this.pkgs);
      } catch (error) {
        console.error('Error fetching packages:', error);
      }
    },
  }
};
</script>

<style scoped>
/* Custom Package Styling */
.custom-package {
  border: 3px solid #309255 !important;
  box-shadow: 0 4px 12px rgba(48, 146, 85, 0.2);
}

.custom-package .info-price {
  background: linear-gradient(135deg, #309255, #28a745);
}

.ml-2 {
  margin-left: 10px;
}

.mt-3 {
  margin-top: 20px;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-link {
  font-weight: 600;
}

.btn-link:hover {
  text-decoration: none !important;
  color: #267a44 !important;
}

/* Enhanced Package Details Styling */
.selected-package-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detailed-list ul li {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detailed-list ul li:last-child {
  border-bottom: none;
}

.detailed-list ul li i {
  color: #309255;
  font-size: 18px;
  margin-right: 8px;
  min-width: 25px;
}

.detailed-list ul li strong {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.detailed-list ul li span {
  font-weight: 600;
  color: #555;
}

.highlight-item {
  background-color: #f0f9f4;
  padding: 15px 10px !important;
  margin: 0 -10px;
  border-left: 4px solid #309255 !important;
}

.value-badge {
  background: #309255;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.unlimited-badge {
  background: #28a745;
  color: white;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
}

.badge-success {
  background: #28a745;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  text-transform: capitalize;
}

.date-item {
  font-size: 13px;
}

.date-item span {
  color: #666;
  font-size: 12px;
}

.status-item {
  background-color: #f8f9fa;
  padding: 12px 10px !important;
  margin: 0 -10px;
}
</style>